import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  ShopifyEligibilityType,
  ShopifyFreeCountriesType,
  ShopifyCustomerGetsType,
  ShopifyCustomerBuysType,
  ShopifyAvailabilityType,
  ShopifyMaximumDiscountUsesType,
  ShopifyActiveDatesType,
  ShopifyCombinationsType,
  PromotionShopifyErrors,
  PromotionShopifyMinimumPurchaseRequirements,
  PromotionShopifyState,
  PromotionShopifyDiscountMethodType,
  PromotionShopifyDiscountType,
  PromotionShopifyDiscountValue,
} from '@/types'
import { getCurrentTimeFormatted } from '@/utils/date'

export const initialPromotionState: PromotionShopifyState = {
  shopifyDiscountType: '',
  shopifyDiscountMethod: {
    method: 'code',
    discountCode: '',
    discountTitle: '',
  },
  shopifyDiscountValue: {
    type: 'fixed',
    fixedValue: '',
    percentageValue: '',
  },
  shopifyMinimumPurchaseRequirements: {
    selectedType: 'none',
    amountValue: '',
    quantityValue: '',
  },
  shopifyCombinations: {
    product: false,
    order: false,
    shipping: false,
  },
  shopifyActiveDates: {
    startDate: getCurrentTimeFormatted().isoString,
    endDate: getCurrentTimeFormatted().isoString,
    hasEndDate: false,
    startTime: getCurrentTimeFormatted().timeString,
    endTime: getCurrentTimeFormatted().timeString,
  },
  shopifyMaximumDiscountUses: {
    discountTotalNumber: false,
    limitOneUsePerCustomer: false,
    discountTotalNumberValue: '',
  },
  shoifyAppliesToSpecific: {
    specificProductsSkus: [],
  },
  shopifyAvailability: {
    pos: false,
  },
  shopifyEligibility: {
    selectedEligibilityType: 'all',
    selectedEligibilitySpecificCustomerSegments: [],
  },
  shopifyCustomerBuys: {
    selectedLowType: 'quantityItems',
    quantityValue: '',
    amountValue: '',
    specificProductsSkus: [],
  },
  shopifyCustomerGets: {
    quantityValue: '',
    specificProductsSkus: [],
    selectedDiscountedType: 'percentage',
    percentageValue: '',
    amountOffEachValue: '',
    maximumNumberOfUsesPerOrder: false,
    maximumNumberOfUsesPerOrderValue: '',
  },
  shopifyFreeCountries: {
    selectedCountryType: 'all',
    selectedCountryArray: [],
    shippingRatesOverCertainAmount: false,
    shippingRatesOverCertainAmountValue: '',
  },
  errors: {},
}

export const promotionSlice = createSlice({
  name: 'promotion',
  initialState: initialPromotionState,
  reducers: {
    setAllShopifyPromotion: (
      state,
      action: PayloadAction<Partial<PromotionShopifyState>>,
    ) => {
      return {
        ...state,
        ...action.payload,
      }
    },
    setShopifyPromotionType: (
      state,
      action: PayloadAction<PromotionShopifyDiscountType>,
    ) => {
      state.shopifyDiscountType = action.payload
    },
    setShopifyDiscountMethod: (
      state,
      action: PayloadAction<Partial<PromotionShopifyDiscountMethodType>>,
    ) => {
      state.shopifyDiscountMethod = {
        ...state.shopifyDiscountMethod,
        ...action.payload,
      }
    },
    setShopifyDiscountValue: (
      state,
      action: PayloadAction<Partial<PromotionShopifyDiscountValue>>,
    ) => {
      state.shopifyDiscountValue = {
        ...state.shopifyDiscountValue,
        ...action.payload,
      }
    },
    setShopifyMinimumPurchaseRequirements: (
      state,
      action: PayloadAction<
        Partial<PromotionShopifyMinimumPurchaseRequirements>
      >,
    ) => {
      state.shopifyMinimumPurchaseRequirements = {
        ...state.shopifyMinimumPurchaseRequirements,
        ...action.payload,
      }
    },
    setShopifyPromotionErrors: (
      state,
      action: PayloadAction<PromotionShopifyErrors>,
    ) => {
      state.errors = {
        ...action.payload,
      }
    },
    setShopifyCombinations: (
      state,
      action: PayloadAction<Partial<ShopifyCombinationsType>>,
    ) => {
      state.shopifyCombinations = {
        ...state.shopifyCombinations,
        ...action.payload,
      }
    },
    setShopifyActiveDates: (
      state,
      action: PayloadAction<Partial<ShopifyActiveDatesType>>,
    ) => {
      state.shopifyActiveDates = {
        ...state.shopifyActiveDates,
        ...action.payload,
      }
    },
    setShopifyAvailability: (
      state,
      action: PayloadAction<Partial<ShopifyAvailabilityType>>,
    ) => {
      state.shopifyAvailability = {
        ...state.shopifyAvailability,
        ...action.payload,
      }
    },
    setShoifyAppliesToSpecific: (
      state,
      action: PayloadAction<{ skus: string[] }>,
    ) => {
      state.shoifyAppliesToSpecific = {
        ...state.shoifyAppliesToSpecific,
        specificProductsSkus: action.payload.skus,
      }
    },
    setShopifyMaximumDiscountUses: (
      state,
      action: PayloadAction<Partial<ShopifyMaximumDiscountUsesType>>,
    ) => {
      state.shopifyMaximumDiscountUses = {
        ...state.shopifyMaximumDiscountUses,
        ...action.payload,
      }
    },
    setShopifyCustomerBuys: (
      state,
      action: PayloadAction<Partial<ShopifyCustomerBuysType>>,
    ) => {
      state.shopifyCustomerBuys = {
        ...state.shopifyCustomerBuys,
        ...action.payload,
      }
    },
    setShopifyCustomerGets: (
      state,
      action: PayloadAction<Partial<ShopifyCustomerGetsType>>,
    ) => {
      state.shopifyCustomerGets = {
        ...state.shopifyCustomerGets,
        ...action.payload,
      }
    },
    setShopifyFreeCountries: (
      state,
      action: PayloadAction<Partial<ShopifyFreeCountriesType>>,
    ) => {
      state.shopifyFreeCountries = {
        ...state.shopifyFreeCountries,
        ...action.payload,
      }
    },
    setShopifyEligibility: (
      state,
      action: PayloadAction<Partial<ShopifyEligibilityType>>,
    ) => {
      state.shopifyEligibility = {
        ...state.shopifyEligibility,
        ...action.payload,
      }
    },
  },
})

export const {
  setAllShopifyPromotion,
  setShopifyDiscountMethod,
  setShopifyPromotionType,
  setShopifyDiscountValue,
  setShopifyMinimumPurchaseRequirements,
  setShopifyCombinations,
  setShopifyPromotionErrors,
  setShopifyActiveDates,
  setShopifyAvailability,
  setShoifyAppliesToSpecific,
  setShopifyMaximumDiscountUses,
  setShopifyCustomerBuys,
  setShopifyCustomerGets,
  setShopifyFreeCountries,
  setShopifyEligibility,
} = promotionSlice.actions

export default promotionSlice.reducer
